import { Header } from '@backstage/core-components';
import { Button, Chip, Typography } from '@material-ui/core';
import type { ReactNode } from 'react';
import type { CockpitHeaderView } from '../model/cockpitViewModel';
import { formatCockpitDateTime } from '../model/formatters';
import { useCockpitStyles } from './cockpitStyles';

export interface CockpitHeaderProps {
  header: CockpitHeaderView;
  refreshing: boolean;
  stale: boolean;
  refreshFailed: boolean;
  onRefresh(): void | Promise<void>;
  onOpenRevision(revision?: number): void | Promise<void>;
  /**
   * Neutral extension port. A host plugin renders its own actions here — the
   * Control Plane injects "Gerenciar conexão" and its operational notice.
   *
   * Deliberately a plain ReactNode and nothing more: this shared package must not
   * learn about Control Plane or Frontier concepts, or the dependency direction
   * would invert. The architecture-boundary test enforces that.
   */
  headerActions?: ReactNode;
}

const HeaderNotice = ({
  refreshing,
  stale,
  refreshFailed,
}: Pick<CockpitHeaderProps, 'refreshing' | 'stale' | 'refreshFailed'>) => {
  if (refreshFailed) {
    return (
      <Typography role="alert">
        Falha ao atualizar. O último retrato válido foi preservado.
      </Typography>
    );
  }
  if (stale) {
    return (
      <Typography role="status">
        Retrato desatualizado há mais de 60 segundos.
      </Typography>
    );
  }
  if (refreshing) {
    return <Typography role="status">Atualização em andamento.</Typography>;
  }
  return null;
};

const HistoricalRevisionActions = ({
  header,
  refreshing,
  onOpenRevision,
}: Pick<CockpitHeaderProps, 'header' | 'refreshing' | 'onOpenRevision'>) => {
  const classes = useCockpitStyles();
  const previousRevision =
    header.revision !== null && header.revision > 1
      ? header.revision - 1
      : null;
  return (
    <>
      {header.historical && (
        <Chip
          label={`Retrato histórico${
            header.revision === null ? '' : ` · revisão ${header.revision}`
          }`}
          variant="outlined"
          className={classes.headerChip}
        />
      )}
      {previousRevision !== null && (
        <Button
          color="inherit"
          onClick={() => void onOpenRevision(previousRevision)}
          disabled={refreshing}
        >
          Revisão anterior
        </Button>
      )}
      {header.historical && (
        <Button
          color="inherit"
          onClick={() => void onOpenRevision()}
          disabled={refreshing}
        >
          Voltar ao retrato atual
        </Button>
      )}
    </>
  );
};

const HeaderActions = ({
  header,
  refreshing,
  stale,
  refreshFailed,
  onRefresh,
  onOpenRevision,
  headerActions,
}: CockpitHeaderProps) => {
  const classes = useCockpitStyles();
  return (
    <div className={classes.headerActions} aria-label="Ações do cockpit">
      {headerActions}
      <Button
        color="inherit"
        variant="outlined"
        disabled={refreshing}
        onClick={() => void onRefresh()}
      >
        {refreshing ? 'Atualizando…' : 'Atualizar dados'}
      </Button>
      <HistoricalRevisionActions
        header={header}
        refreshing={refreshing}
        onOpenRevision={onOpenRevision}
      />
      <HeaderNotice
        refreshing={refreshing}
        stale={stale}
        refreshFailed={refreshFailed}
      />
    </div>
  );
};

export const CockpitHeader = (props: CockpitHeaderProps) => {
  const { header } = props;
  const subtitle = `${header.viaLabel} · ${header.statusLabel} · ${
    header.evidenceLabel
  } · Atualizado em ${formatCockpitDateTime(header.updatedAt)}`;
  return (
    <Header title={header.title} subtitle={subtitle}>
      <HeaderActions {...props} />
    </Header>
  );
};
